import React, { lazy, Suspense, useEffect, useRef, useState } from "react";
import BannerWithLogo from "../components/BannerWithLogo";
import AutoScrollBanner from "../components/AutoScrollBanner";
import ImageTextSection from "../components/ImageTextSection";
import FeatureIconsSection from "../components/FeatureIconsSection";
import AllergenFreeSection from "../components/AllergenFreeSection";
import FetaHighlightSection from "../components/FetaHighlightSection";
import FounderIntroSection from "../components/FounderIntroSection";
import RecipeHighlightSection from "../components/RecipeHighlightSection";
import AppPromoSection from "../components/AppPromoSection";
import BlogPreviewSection from "../components/BlogPreviewSection";

import logoAboveTitle from "../assets/logo.webp";
import { socialLinks } from "../components/Footer";
import PageMeta from "../components/PageMeta";

// 🖼 Example icons/images for banner items  ---   "../assets/icons/leaf.png";
import icon1 from "../assets/cheese.webp";
import icon2 from "../assets/burger.webp";
import icon3 from "../assets/plant.webp";

import fetaImage from "../assets/revo-main-image.sm.webp";       // main left image
import cheesePattern from "../assets/revo-overlay-image.webp";


// FeatureIconsSection######
import iconPlant from "../assets/iconPlant.webp";
import iconQuality from "../assets/iconQuality.webp";
import iconInnovation from "../assets/iconInnovation.webp";
import iconSustainability from "../assets/iconSustainability.webp";
import iconCommunity from "../assets/iconCommunity.webp";


// AllergenFreeSection######
import zeroDairy from "../assets/zeroDairy.webp";
import zeroGluten from "../assets/zeroGluten.webp";
import zeroNuts from "../assets/zeroNuts.webp";
import zeroSoy from "../assets/zeroSoy.webp";
import productImage from "../assets/productImage.sm.webp";



// FetaHighlightSection ############
import fetaVideo from "../assets/videos/feta-section-video.mp4";
import fetaPoster from "../assets/fetaPoster.webp";
import chefImage from "../assets/chefImage.webp";
import reviewer from "../assets/reviewer.webp";



// FounderIntroSection ##########
import founderImage from "../assets/founderImage.sm.webp";
import signatureImg from "../assets/signatureImg.webp";



// RecipeHighlightSection ##########
import recipeVideo from "../assets/videos/recipeVideo.mp4";
import recipePoster from "../assets/recipePoster.webp";


// ReviewSliderSection###############
import reviewerStrudelle from "../assets/reviewerStrudelle.webp";
import reviewerJessica from "../assets/reviewerJessica.webp";
import reviewerVic from "../assets/reviewerVic.webp";
import reviewerOdiliejp from "../assets/reviewerOdiliejp.webp";
import reviewerNikki from "../assets/reviewerNikki.webp";
import reviewerRiris from "../assets/reviewerRiris.webp";
import reviewerArie from "../assets/reviewerArie.webp";
import reviewerjOulie18 from "../assets/reviewerjOulie18.webp";



// AppPromoSection ############
import appPromoImageDesktop from "../assets/make-switch-desktop.webp";
import appPromoImageMobile from "../assets/make-switch-mobile.webp";
// import CtaSection from "../components/CtaSection";

const ReviewSliderSection = lazy(() => import("../components/ReviewSliderSection"));

function WhenVisible({ children, minHeight = 280 }) {
  const ref = useRef(null);
  const [show, setShow] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    if (typeof IntersectionObserver === "undefined") {
      setShow(true);
      return undefined;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        setShow(true);
        observer.disconnect();
      },
      { rootMargin: "400px" }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={ref} style={show ? undefined : { minHeight }}>
      {show ? children : null}
    </div>
  );
}

export default function Home() {
  const bannerItems = [
    { image: icon1, text: "Smoother Taste!" },
    { image: icon2, text: "Making #Veganism Mainstream" },
    { image: icon3, text: "If You Plant It, It Will Grow!" },
    { image: icon1, text: "There's a New Cheese in Town" },
    { image: icon3, text: "Make the Plant-Based Switch" },
  ];

  const featureItems = [
    { icon: iconPlant, title: "100% Plant Based" },
    { icon: iconQuality, title: "High Quality" },
    { icon: iconInnovation, title: "Innovation" },
    { icon: iconSustainability, title: "Sustainability" },
    { icon: iconCommunity, title: "Community" },
  ];

  const allergenFeatures = [
    { icon: zeroDairy, label: "Zero Dairy" },
    { icon: zeroGluten, label: "Zero Gluten" },
    { icon: zeroNuts, label: "Zero Nuts" },
    { icon: zeroSoy, label: "Zero Soy" },
  ];

  const reviews = [
  {
    text: "Brandon and I lost our minds over this cheese...Best vegan cheese I’ve had to date!",
    name: "Strudelle",
    image: reviewerStrudelle,
  },
  {
    text: "Your feta style block BLOWS my mind",
    name: "Jessica Jurkowsi",
    image: reviewerJessica,
  },
  {
    text: "Love the assortment!",
    name: "Vic and Chris",
    image: reviewerVic,
  },
  {
    text: "trying this new vegan cheese for dinner",
    name: "Odiliejp",
    image: reviewerOdiliejp,
  },
  {
    text: "These are amazing and totally soy free!",
    name: "NIKKI",
    image: reviewerNikki,
  },
  {
    text: "Nafsika's Garden is LIFECHANGING",
    name: "Riris Recipes",
    image: reviewerRiris,
  },
  {
    text: "The best Vegan Cheeses ever!!!",
    name: "Arie Reiser",
    image: reviewerArie,
  },
  {
    text: "BEST VEGAN CHEESE. LIKE EVER",
    name: "jOulie18",
    image: reviewerjOulie18,
  }
];

  return (
    <>
      <PageMeta
        title="Vegan Cheese & Plant-Based Foods | Nafsika's Garden"
        description="Discover Nafsika's Garden vegan cheese and plant-based foods. Enjoy delicious, gluten-free, soy-free, nut-free, and dairy-free cheese and spreads."
      />
      <BannerWithLogo
        title="WELCOME TO THE GARDEN"
        description="Nafsika’s Garden is a pioneering plant-based food brand that has emerged as a leader in the industry, providing a diverse range of delectable and nourishing plant-based products."
        logoImage={logoAboveTitle}
        buttonText="Locate Stores"
        buttonLink="/store-locator"
      />

      <AutoScrollBanner
        items={bannerItems}
        bgColor="bg-[#f8f8f8]"
        textColor="text-gray-800"
        speed={30}
      />

      <div className="my-16 max-w-7xl mx-auto px-4">
        <ImageTextSection
          image={fetaImage}
          imageAlt="Bowl of orange soup topped with vegan feta and parsley beside a Mafrika’s Garden Feta Style container and spoon."
          bgPatternAlt="Cheese pattern"
          title="Revolutionizing Cheese"
          description="In a world where culinary innovation dances with ethics, vegan cheese and its dairy-free companions lead a flavorful revolution. As consciousness blooms, seekers of diverse experiences embrace compassionate alternatives without sacrificing taste. This plant-based movement, a symphony of artistry and sustainability, captivates gourmands and conscious consumers alike. Talented artisans craft textures and flavors, weaving the essence of plant-based ingredients and seasonings. This gastronomic awakening harmonizes indulgence with compassion, whispering a promise of a kinder, more delicious world."
          bgColor="bg-[#C03CA0]"
          bgPattern={cheesePattern}
        />
      </div>

      <FeatureIconsSection features={featureItems} />

      <AllergenFreeSection
        title="There’s a new cheese in town"
        highlightWord="new cheese"
        description="Nafsika’s Garden product line started off with just vegan cheese and is quickly expanding to an array of dairy-free/vegan food products."
        subheading="Currently, our dairy-free line is completely allergen free"
        features={allergenFeatures}
        note="That’s not all. Not only does our cheese look and feel the same, some will argue that they cannot tell the difference between ours and “the real thing.”"
        image={productImage}
        imageAlt="A variety of Nafsika’s Garden brand vegan cheese, yogurt, and cream cheese products."
      />

      
      <FetaHighlightSection
        videoSrc={fetaVideo}
        videoPoster={fetaPoster}
        tag="Melt’s to perfection"
        title="Our feta is betta"
        description="It shares the same taste and texture as real feta, with a smoother taste! It crumbles like feta and makes a perfect addition as table cheese, as well as in salads, pastries, and baking."
        buttonText="Locate Stores"
        buttonLink="/store-locator"
        chefImage={chefImage}
        reviewerImage={reviewer}
        reviewerName="Nikki"
        reviewerText="These are amazing and totally soy free"
        rating={5}
      />

      <FounderIntroSection
        title="Meet Nafsika Antypas,"
        highlight1="Nafsika Antypas"
        highlight2="Face"
        description="Nafsika Antypas is a writer, TV producer, actress, author and entrepreneur. Her motto has always been,"
        quote="If You Plant It, It Will Grow."
        signature={signatureImg}
        buttonText="View Bio"
        buttonLink="https://nafsikaantypas.com/"
        mainImage={founderImage}
        mainImageAlt="Warm-toned collage of a smiling woman beside tomatoes and greens, with radishes and herbs on a black background."
        overlayTitle="Nafsika’s Social Media"
        socialLinks={socialLinks}
        // socialLinks={{
        //   facebook: "https://facebook.com/",
        //   x: "https://twitter.com/",
        //   instagram: "https://instagram.com/",
        //   linkedin: "https://linkedin.com/",
        // }}
      />

      <RecipeHighlightSection
        title="Spinach Artichoke Dip: A Cheesy, Plant-Based Delight"
        highlight="Plant-Based"
        description="Get ready to dig into a warm and creamy Spinach Artichoke Dip that’s loaded with flavour and perfect for sharing! This rich, cheesy dip combines the earthiness of spinach with the subtle tang of artichoke, all enveloped in the melt-in-your-mouth goodness of Nafsika’s Garden Mozzarella, Parmesan, and cream cheese style cheeses. Ideal for parties, gatherings, or a cozy night in, this dip will quickly become your go-to for a plant-based, crowd-pleasing appetizer. Let’s dive into this easy, delicious recipe!"
        buttonText="Go to Recipe"
        buttonLink="/recipes/spinach-artichoke-dip"
        videoSrc={recipeVideo}
        videoPoster={recipePoster}
      />

      <WhenVisible minHeight={360}>
        <Suspense fallback={null}>
          <ReviewSliderSection reviews={reviews} />
        </Suspense>
      </WhenVisible>

      {/* <CtaSection /> */}

      <WhenVisible minHeight={520}>
        <BlogPreviewSection />
      </WhenVisible>

      <WhenVisible minHeight={360}>
      <AppPromoSection
        titleStart="Make the"
        highlight="Plant-based Switch"
        description="Get access to weekly guidance and materials, lesson plans, daily menus and curated recipes."
        // tagline="Tell your brand's story through imagesss"
        appStoreLink="https://apps.apple.com/in/app/the-plant-based-switch/id6444078958"
        googlePlayLink="https://play.google.com/store/apps/details?id=com.plant.based.food&pli=1"
        desktopImage={appPromoImageDesktop}
        mobileImage={appPromoImageMobile}
      />
      </WhenVisible>

    </>
  );
}
